/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-danger */
import { CircularProgress, Theme, useMediaQuery } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import ReplayIcon from '@material-ui/icons/Replay';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ModalEditImg, {
  getModalImageHeightForTemplate,
  MODAL_IMAGE_WIDTH,
} from '@orientaction/components/Admin/Game/ModalEditImg';
import { getFromLS, getRestartedResultId, isAuthenticated } from '@orientaction/utils';
import { find, includes } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { claimShareResourcesAfterAuth } from '../../../../utils/claimAuthorizedResource';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { MediaVideoPlayer, RedesignLayout } from '../../shared';
import RedesignImage from '../../shared/components/RedesignImage';
import InlineSignup from '../../shared/InlineSignup';
import ChoiceOption from './components/ChoiceOption';
import { useSeriousGamePlayStyles } from './index.styles';

type GameChoice = {
  id: number;
  text: string;
  name?: string;
  destination?: number;
  destinationUrl?: string;
  selectedVariable?: number;
};

type GamePage = {
  id: number;
  template_game?: number;
  typeChoice?: string;
  choiceDisplay?: string;
  choiceTitle?: string;
  choice?: GameChoice[];
  contentForType1?: string;
  contentForType2?: string;
  p1?: string;
  p2?: string;
  citationContent1?: string;
  citationContent2?: string;
  citationAuthor1?: string;
  citationAuthor2?: string;
  informationEncyclopedia1?: string;
  informationEncyclopedia2?: string;
  illustration?: {
    url?: string;
  };
  video?: string;
  videoEn?: string | null;
  autoPlayVideoEn?: number | null;
  muteVideoEn?: number | null;
  autoPlayVideo?: number;
  muteVideo?: number;
  textEndGame?: string;
  isVariable?: boolean;
  isEndVariable?: boolean;
  idRestitution?: number;
};

type GameResult = {
  idGame: number;
  idResult: number;
  typeGame?: string;
  couverture?: string;
  pages: GamePage[];
  idViewedPages?: number[] | string;
  idViewedVariables?: number[] | string;
  progress?: number;
  status?: string;
  isCompletedSynthesis?: boolean;
  last_id_page_visited?: number;
  idRestitution?: string | number;
  idLastRestitution?: string | number;
  variables_restitution?: Array<{
    id: number;
    page_restitution: GamePage[];
  }>;
};

type RenderBlock = 'content1' | 'content2' | 'media' | 'choices';

const parseIdList = (value: number[] | string | undefined) => {
  if (Array.isArray(value)) {
    return value.map(Number);
  }

  if (typeof value === 'string') {
    return value.split(',').filter(Boolean).map(Number);
  }

  return [];
};

const getTemplateBlocks = (template?: number): RenderBlock[] => {
  switch (template) {
    case 1:
      return ['media', 'content2', 'content1', 'choices'];
    case 2:
      return ['content1', 'media', 'content2', 'choices'];
    case 3:
      return ['content1', 'content2', 'choices'];
    case 4:
      return ['media', 'content1', 'choices'];
    case 5:
      return ['content1', 'media', 'choices'];
    case 6:
      return ['media', 'choices'];
    case 7:
      return ['choices', 'media'];
    default:
      return ['content1', 'media', 'content2', 'choices'];
  }
};

const getMostFrequentElement = (items: number[]) => {
  const countMap = items.reduce<Record<number, number>>((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
  const maxFrequency = Math.max(...Object.values(countMap));
  const mostFrequentItems = Object.entries(countMap)
    .filter(([, count]) => count === maxFrequency)
    .map(([id, count]) => ({ id: Number(id), number: count }));

  return {
    ...mostFrequentItems[0],
    equality: mostFrequentItems.length > 1,
  };
};

const RedesignSeriousGamePlay = () => {
  const classes = useSeriousGamePlayStyles();
  const token = getFromLS('userToken') || '';
  const isDisconnected = !token;
  const isUserConnected = isAuthenticated();
  const history = useHistory();
  const location = useLocation();
  const { id, idLastPage }: { id: string; idLastPage?: string } = useParams();
  const readOnly = new URLSearchParams(location.search).get('readOnly') === 'true';
  const isXS = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));

  const [game, setGame] = useState<GameResult | null>(null);
  const [page, setPage] = useState<GamePage | null>(null);
  const [pagesViewed, setPagesViewed] = useState<number[]>([]);
  const [variablesSelected, setVariablesSelected] = useState<number[]>([]);
  const [pageRestitution, setPageRestitution] = useState<GamePage[]>([]);
  const [indexPR, setIndexPR] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<GameChoice | null>(null);
  const [zoomUrl, setZoomUrl] = useState('');
  const [zoomImageDimensions, setZoomImageDimensions] = useState<{
    width?: number;
    height?: number;
  }>({});
  const [showSignup, setShowSignup] = useState(false);
  const [modelsRendered, setModelsRendered] = useState(0);
  const pendingChoiceRef = useRef<{ pageId: number; idChoice: number } | null>(null);
  const restitutionIdRef = useRef<number | null>(null);
  const [canOpenSynthesis, setCanOpenSynthesis] = useState(false);

  const syncSynthesisFlag = (value?: boolean) => {
    if (typeof value !== 'boolean') {
      return;
    }
    setCanOpenSynthesis(value);
  };

  const blocks = useMemo(() => getTemplateBlocks(page?.template_game), [page?.template_game]);
  const modalImageHeight = useMemo(
    () => getModalImageHeightForTemplate(page?.template_game),
    [page?.template_game]
  );

  useEffect(() => {
    const fetchGameDetails = async () => {
      setLoading(true);

      try {
        const response: GameResult = await apiServiceNative.get(`/api/game-front-user/${id}`);
        syncSynthesisFlag(response?.isCompletedSynthesis);

        setGame(response);
        setPagesViewed(parseIdList(response.idViewedPages));
        setVariablesSelected(parseIdList(response.idViewedVariables));
        setProgress(response.progress || 0);
        restitutionIdRef.current = Number(response.idRestitution) || null;

        const requestedPageId = Number(idLastPage);
        if (readOnly && requestedPageId) {
          setPage(find(response.pages, { id: requestedPageId }) || response.pages[0]);
        } else if (response?.last_id_page_visited !== 0) {
          if (response?.idRestitution && response?.idLastRestitution) {
            const pageRest = find(response.variables_restitution, {
              id: Number(response.idRestitution),
            });
            const restitutionPages = pageRest?.page_restitution || [];
            const pageLast = find(restitutionPages, {
              id: Number(response.idLastRestitution),
            });
            const indexPageRest = restitutionPages.findIndex(
              item => item.id === Number(response.idLastRestitution)
            );

            setPageRestitution(restitutionPages);
            setPage(
              pageLast
                ? {
                    ...pageLast,
                    isVariable: true,
                    isEndVariable: restitutionPages.length === indexPageRest + 1,
                    idRestitution: Number(response.idRestitution),
                  }
                : response.pages[0]
            );
            setIndexPR(indexPageRest + 1);
          } else {
            setPage(
              find(response.pages, { id: response.last_id_page_visited }) || response.pages[0]
            );
          }
        } else {
          setPage(response.pages[0]);
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
  }, [id, idLastPage, readOnly]);

  useEffect(() => {
    setSelectedChoice(null);
  }, [page?.id]);

  useEffect(() => {
    const updateLastViewPage = async (pageToEdit: GamePage, gameToEdit: GameResult) => {
      if (readOnly || gameToEdit.status === 'completed') {
        return;
      }

      if (pageToEdit?.isVariable) {
        const restitutionId = Number(pageToEdit.idRestitution || restitutionIdRef.current);
        if (!restitutionId || !pageToEdit.id) {
          return;
        }
        restitutionIdRef.current = restitutionId;
        const updatedRestitution: GameResult = await apiServiceNative.put(
          `/api/update-result-game/${gameToEdit.idResult}`,
          {
            idRestitution: restitutionId,
            idLastRestitution: pageToEdit.id,
          }
        );
        syncSynthesisFlag(updatedRestitution?.isCompletedSynthesis);
        return;
      }

      const viewedPages = pagesViewed || [];
      const pendingChoice = pendingChoiceRef.current;
      pendingChoiceRef.current = null;

      if (pageToEdit?.typeChoice === 'end_game') {
        if (pendingChoice) {
          await apiServiceNative.put(`/api/update-result-game/${gameToEdit.idResult}`, {
            last_id_page_visited: pendingChoice.pageId,
            date_consultation: new Date(),
            choicesResultsData: JSON.stringify({
              pageId: pendingChoice.pageId,
              idChoice: pendingChoice.idChoice,
            }),
          });
        }

        const idViewedPages = includes(viewedPages, pageToEdit.id)
          ? viewedPages
          : [...viewedPages, pageToEdit.id];

        const updatedGame: GameResult = await apiServiceNative.put(
          `/api/update-result-game/${gameToEdit.idResult}`,
          {
            last_id_page_visited: pageToEdit.id,
            idViewedPages: idViewedPages.join(','),
          }
        );
        syncSynthesisFlag(updatedGame?.isCompletedSynthesis);
        setPagesViewed(parseIdList(updatedGame.idViewedPages));
        setVariablesSelected(parseIdList(updatedGame.idViewedVariables));
        setProgress(updatedGame.progress || 0);
        return;
      }

      let dataToSend: Record<string, unknown> = {
        last_id_page_visited: pageToEdit.id,
        date_consultation: new Date(),
      };

      if (viewedPages.length === 0 || !includes(viewedPages, pageToEdit.id)) {
        dataToSend = {
          ...dataToSend,
          idViewedPages: [...viewedPages, pageToEdit.id].join(','),
          idViewedVariables: variablesSelected.join(','),
        };
      }

      if (pendingChoice) {
        dataToSend.choicesResultsData = JSON.stringify({
          pageId: pendingChoice.pageId,
          idChoice: pendingChoice.idChoice,
        });
      }

      const updatedGame: GameResult = await apiServiceNative.put(
        `/api/update-result-game/${gameToEdit.idResult}`,
        dataToSend
      );
      syncSynthesisFlag(updatedGame?.isCompletedSynthesis);
      setPagesViewed(parseIdList(updatedGame.idViewedPages));
      setVariablesSelected(parseIdList(updatedGame.idViewedVariables));
      setProgress(updatedGame.progress || 0);
    };

    if (page && game) {
      updateLastViewPage(page, game);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page, game, readOnly]);

  useEffect(() => {
    if (!page || loading) return;
    setModelsRendered(prev => prev + 1);
  }, [page?.id, loading]);

  useEffect(() => {
    if (isDisconnected) {
      return;
    }
    const userId = Number(JSON.parse(getFromLS('userInfos') || '{}')?.id);
    if (!userId) {
      return;
    }
    claimShareResourcesAfterAuth({ userId }).catch(() => undefined);
  }, [isDisconnected]);

  useEffect(() => {
    if (!isDisconnected || showSignup) return;

    if (modelsRendered >= 4) {
      setShowSignup(true);
    }
  }, [isDisconnected, modelsRendered, showSignup]);

  const goBackResource = () => {
    if (readOnly) {
      const params = new URLSearchParams(location.search);
      const next = new URLSearchParams();
      if (params.get('from') === 'admin') {
        next.set('from', 'admin');
      }
      const userId = params.get('userId');
      if (userId) {
        next.set('userId', userId);
      }
      const query = next.toString();
      history.push(query ? `/game/result/${id}?${query}` : `/game/result/${id}`);
      return;
    }

    if (isXS && game) {
      sessionStorage.setItem('redirect', JSON.stringify(['seriousgame', game.idGame]));
    }

    history.push('/mes-ressources');
  };

  const restartGameToCover = async () => {
    if (!game) return;

    const response = await apiServiceNative.put(`/api/restart-game-result/${game.idResult}`, {});
    const newIdResult = getRestartedResultId(response);
    if (!newIdResult) {
      return;
    }
    history.push(`/game/view/${game.idGame}/${newIdResult}`);
  };

  const goToDestination = async (idDestination: number | undefined) => {
    if (!game || !idDestination) return;

    if (idDestination === -1) {
      await restartGameToCover();
      return;
    }

    setPage(find(game.pages, { id: idDestination }) || null);
  };

  const checkVariableSelected = (idVariableSelected?: number) => {
    if (!idVariableSelected) return;
    setVariablesSelected(prev => [...prev, idVariableSelected]);
  };

  const goToEndDestination = () => {
    if (!game) return;

    if (readOnly || canOpenSynthesis) {
      history.push(`/game/result/${game.idResult}`);
      return;
    }

    history.push('/mes-ressources');
  };

  const endGameVariable = () => {
    goToEndDestination();
  };

  const endGame = async () => {
    if (!game) return;

    if (game.typeGame === 'game' && variablesSelected.length > 0) {
      const { id: idFrequent, equality } = getMostFrequentElement(variablesSelected);
      const variableRestToShow = find(game.variables_restitution, { id: idFrequent });
      const restitutionPages = variableRestToShow?.page_restitution || [];

      if (restitutionPages.length > 0) {
        setPageRestitution(restitutionPages);
        restitutionIdRef.current = variableRestToShow?.id ?? null;
        setPage(
          restitutionPages[0]
            ? {
                ...restitutionPages[0],
                isVariable: true,
                idRestitution: variableRestToShow?.id,
                isEndVariable: !equality && restitutionPages.length === 1,
                template_game: equality ? 8 : restitutionPages[0].template_game,
              }
            : null
        );
        setIndexPR(1);
        return;
      }
    }

    goToEndDestination();
  };

  const nextPageRestitution = () => {
    if (pageRestitution.length >= indexPR && pageRestitution[indexPR]) {
      setPage({
        ...pageRestitution[indexPR],
        isVariable: true,
        isEndVariable: pageRestitution.length === indexPR + 1,
        idRestitution: page?.idRestitution || restitutionIdRef.current || undefined,
      });
      setIndexPR(indexPR + 1);
    }
  };

  const chooseChoice = (choice: GameChoice) => {
    if (readOnly) return;

    setSelectedChoice(choice);

    if (page?.typeChoice === 'redirection' && choice.destinationUrl) {
      window.open(choice.destinationUrl, '_blank');
    }
  };

  const validateChoice = () => {
    if (readOnly || !selectedChoice || !page) return;

    pendingChoiceRef.current = { pageId: page.id, idChoice: selectedChoice.id };
    checkVariableSelected(selectedChoice.selectedVariable);
    goToDestination(selectedChoice.destination);
  };

  const pursue = () => {
    if (readOnly) return;
    const firstChoice = page?.choice?.[0];
    if (page && firstChoice?.id != null) {
      pendingChoiceRef.current = { pageId: page.id, idChoice: firstChoice.id };
    }
    checkVariableSelected(firstChoice?.selectedVariable);
    goToDestination(firstChoice?.destination);
  };

  const renderContentBlock = (slot: 1 | 2) => {
    const contentType = slot === 1 ? page?.contentForType1 : page?.contentForType2;
    const html = slot === 1 ? page?.p1 : page?.p2;
    const citation = slot === 1 ? page?.citationContent1 : page?.citationContent2;
    const author = slot === 1 ? page?.citationAuthor1 : page?.citationAuthor2;
    const encyclopedia =
      slot === 1 ? page?.informationEncyclopedia1 : page?.informationEncyclopedia2;

    if (contentType === 'citation' && citation) {
      return (
        <figure className={classes.citation}>
          <blockquote className={classes.citationText}>{citation}</blockquote>
          {author && <figcaption className={classes.citationAuthor}>{author}</figcaption>}
        </figure>
      );
    }

    if (contentType === 'encyclopedia' && encyclopedia) {
      return (
        <div
          className={`${classes.encyclopedia} editor`}
          dangerouslySetInnerHTML={{ __html: encyclopedia }}
        />
      );
    }

    if (!html) return null;

    return (
      <div className={`${classes.richText} editor`} dangerouslySetInnerHTML={{ __html: html }} />
    );
  };

  const openZoom = (url: string) => {
    setZoomUrl(url);
    const img = new Image();
    img.src = url;
    img.onload = () => {
      setZoomImageDimensions({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      setZoomImageDimensions({});
    };
  };

  const closeZoom = () => {
    setZoomUrl('');
    setZoomImageDimensions({});
  };

  const renderMediaBlock = () => {
    const illustrationUrl = page?.illustration?.url;

    if (!illustrationUrl && !page?.video && !page?.videoEn) return null;

    return (
      <div className={classes.mediaBlock}>
        {illustrationUrl && (
          <div className={classes.imageWrap}>
            <button
              type="button"
              className={classes.zoomButton}
              aria-label="Agrandir l'image"
              onClick={() => openZoom(illustrationUrl)}>
              <ZoomInIcon fontSize="small" />
            </button>
            <RedesignImage
              priority={true}
              className={classes.mediaImage}
              src={illustrationUrl}
              alt="Illustration de la page"
            />
          </div>
        )}
        {(page?.video || page?.videoEn) && (
          <MediaVideoPlayer
            {...page}
            videoUrl={page.video}
            autoPlayVideo={page.autoPlayVideo || 0}
            muteVideo={page.muteVideo || 0}
          />
        )}
      </div>
    );
  };

  const renderChoices = () => {
    if (!page) return null;

    const finishLabel = readOnly
      ? 'Retour à la synthèse'
      : canOpenSynthesis
      ? 'Voir le résultat'
      : 'Terminer';

    if (page.isVariable && !page.isEndVariable) {
      if (readOnly) return null;
      return (
        <button type="button" className={classes.primaryButton} onClick={nextPageRestitution}>
          Suivant
        </button>
      );
    }

    if (page.isVariable && page.isEndVariable) {
      return (
        <button type="button" className={classes.primaryButton} onClick={endGameVariable}>
          {finishLabel}
        </button>
      );
    }

    if (page.template_game === 8) {
      return (
        <button type="button" className={classes.primaryButton} onClick={endGameVariable}>
          {finishLabel}
        </button>
      );
    }

    if (page.typeChoice === 'end_game') {
      return (
        <div className={classes.choices}>
          <p className={classes.endMessage}>
            {game?.typeGame === 'game' ? 'Le test est terminé' : 'Ceci est la fin du jeu. Merci.'}
          </p>
          <button
            type="button"
            className={classes.primaryButton}
            onClick={readOnly ? endGameVariable : endGame}>
            {readOnly ? 'Retour à la synthèse' : page.textEndGame || finishLabel}
          </button>
        </div>
      );
    }

    if (page.typeChoice === 'pursue') {
      if (readOnly) return null;
      return (
        <button
          type="button"
          className={classes.primaryButton}
          onClick={pursue}
          aria-label={page.choice?.[0]?.text || 'Suivant'}>
          <span dangerouslySetInnerHTML={{ __html: page.choice?.[0]?.text || 'Suivant' }} />
        </button>
      );
    }

    if (!page.choice?.length) return null;

    return (
      <div className={classes.choices}>
        {page.choiceTitle && <p className={classes.choiceTitle}>{page.choiceTitle}</p>}
        <div className={classes.choices}>
          {page.choice.map((choice, index) => {
            const isSelected = selectedChoice?.id === choice.id;

            return (
              <ChoiceOption
                key={choice.id}
                label={`Choix ${index + 1}`}
                text={choice.name || choice.text}
                selected={isSelected}
                disabled={readOnly}
                onSelect={() => chooseChoice(choice)}
              />
            );
          })}
        </div>
        {page.typeChoice === 'multiples' && !readOnly && (
          <button
            type="button"
            className={classes.primaryButton}
            disabled={!selectedChoice}
            onClick={validateChoice}>
            <span>Valider mon choix</span>
            <CheckIcon className={classes.primaryButtonIcon} />
          </button>
        )}
      </div>
    );
  };

  const renderBlock = (block: RenderBlock) => {
    if (block === 'content1') return renderContentBlock(1);
    if (block === 'content2') return renderContentBlock(2);
    if (block === 'media') return renderMediaBlock();
    return renderChoices();
  };

  return (
    <RedesignLayout
      isaddBackground={false}
      noPadding={true}
      footer={false}
      showNav={!isDisconnected}
      isUserConnected={!isDisconnected}
      disableOverflowX={true}>
      <main className={classes.page}>
        <div className={classes.shell}>
          <div className={classes.actions}>
            <button type="button" className={classes.actionButton} onClick={goBackResource}>
              <KeyboardArrowLeftIcon className={classes.actionIcon} />
              Retour
            </button>
            {!readOnly && (
              <button type="button" className={classes.actionButton} onClick={restartGameToCover}>
                <ReplayIcon className={classes.actionIcon} />
                Recommencer
              </button>
            )}
          </div>

          {showSignup ? (
            <div className={classes.signupWrap}>
              <InlineSignup
                testImage={game?.couverture}
                gameResultId={String(game?.idResult || id)}
                authorizedGameId={game?.idGame}
                contentType="game"
                onGameLinked={() => setShowSignup(false)}
              />
            </div>
          ) : (
            <div className={classes.cardWrap}>
              <section className={classes.card}>
                {loading || !page ? (
                  <div className={classes.loadingCenter}>
                    <CircularProgress />
                  </div>
                ) : (
                  <>
                    <div className={classes.cardInner}>
                      <div className={classes.progressBlock}>
                        <div className={classes.progressMeta}>
                          <span>Progression</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <div className={classes.progressTrack} aria-hidden={true}>
                          <div
                            className={classes.progressFill}
                            style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
                          />
                        </div>
                      </div>
                      <div className={classes.contentStack}>
                        {blocks.map(block => {
                          const content = renderBlock(block);
                          return content ? <div key={block}>{content}</div> : null;
                        })}
                      </div>
                    </div>
                    {!blocks.includes('choices') && (
                      <div className={classes.footer}>{renderChoices()}</div>
                    )}
                  </>
                )}
              </section>
            </div>
          )}
        </div>

        <ModalEditImg
          open={Boolean(zoomUrl)}
          fileUrl={zoomUrl}
          handleClose={closeZoom}
          viewOnly={true}
          width={MODAL_IMAGE_WIDTH}
          height={modalImageHeight}
          imgHeightB={zoomImageDimensions.height}
          imgWidthB={zoomImageDimensions.width}
        />
      </main>
    </RedesignLayout>
  );
};

export default RedesignSeriousGamePlay;
